import { useRouterState } from '@tanstack/react-router';
import { HelpCircle, Menu, Search } from 'lucide-react';
import { useAuth } from '@/components/providers/auth-provider';
import { useCommandPaletteStore } from '@/stores/command-palette-store';
import { t } from '@/lib/i18n';
import { ProfileMenu } from './profile-menu';
import { TopbarNotificationsBell } from './topbar-notifications-bell';

/**
 * Topbar — 56px chrome that pins to the top of the work column. Renders
 * the org → page breadcrumb on the leading edge and a ⌘K search pill
 * + notification + help icons on the trailing edge.
 *
 * The search pill is intentionally dumb: it's a click target that opens
 * the existing `<CommandPalette>`. PR1 doesn't refactor the palette
 * itself; that's a later PR.
 */

const PAGE_LABELS: Record<string, string> = {
  '/': 'Briefing',
  '/today': 'Briefing',
  '/briefing': 'Briefing',
  '/ask': 'Ask',
  '/dossiers': 'Dossiers',
  '/scorecard': 'Scorecard',
  '/rocks': 'Rocks',
  '/playbook': 'Playbook',
  '/insights': 'Insights',
  '/signals': 'Signals',
  '/signals/external': 'Outside view',
  '/alerts': 'Alerts',
  '/todos': 'Todos',
  '/agent': 'Agent log',
  '/agent/rehearsal': 'Agent rehearsal',
  '/sources': 'Sources',
  '/discovery': 'How we read you',
  '/notifications': 'Notifications',
  '/notes': 'Notes',
  '/admin/people': 'People',
  '/admin/audit': 'Audit log',
  '/admin/settings': 'Settings',
  '/admin/connectors': 'Connectors',
  '/admin/connectors/trust': 'Source trust',
  '/admin/entity-resolution': 'Entity resolution',
  '/governance': 'Governance',
  '/governance/calendar': 'Governance · calendar',
  '/governance/help': 'Governance · help',
  '/accountability': 'Accountability',
  '/accountability/chart': 'Org chart',
  '/bookmarks': 'Bookmarks',
  '/settings/org': 'Org settings',
  '/settings/org/team': 'Team',
  '/settings/org/members': 'Team', // legacy URL → unified team page
  '/settings/org/invites': 'Team', // legacy URL → unified team page
  '/admin/billing': 'Billing & subscription',
  '/profile': 'Profile',
  '/empty': 'Welcome',
  '/superadmin': 'Superadmin',
  '/superadmin/users': 'Superadmin · users',
  '/superadmin/audit': 'Superadmin · audit',
  '/superadmin/orgs/new': 'Superadmin · new org',
  // EOS-cadence pages: direct-URL access only, not promoted in the
  // sidebar per CLAUDE.md scope.
  '/issues': 'Issues',
  '/l10': 'L10',
};

interface PortalTopbarProps {
  /**
   * Callback for the responsive hamburger button. Mounted by the
   * container query at ≤1180px (`.portal-topbar-hamburger`). The shell
   * passes a handler that flips its `.is-side-revealed` modifier.
   */
  onMenuClick?: () => void;
  /** Whether the sidebar drawer is currently revealed (for ARIA state). */
  menuOpen?: boolean;
}

export function PortalTopbar({ onMenuClick, menuOpen = false }: PortalTopbarProps) {
  const { activeOrg } = useAuth();
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const orgName = activeOrg?.name ?? 'LEANOPX';
  const pageLabel = PAGE_LABELS[path] ?? deriveLabel(path);

  return (
    <header className="portal-top">
      {/* v6 accent sweep — a 1px horizontal gold line that crosses the
       * bottom edge of the topbar every 9.6s. Styles + keyframes live
       * in `packages/design-system/src/tokens/base.css`
       * (`.ds-topbar-sweep` / `.ds-topbar-sweep-trail` /
       * `@keyframes ds-topbar-sweep`). Disabled under
       * `prefers-reduced-motion: reduce`. */}
      <span aria-hidden className="ds-topbar-sweep">
        <span className="ds-topbar-sweep-trail" />
      </span>
      <div className="flex items-center gap-2.5 font-mono text-[11.5px] tracking-[0.04em] text-[var(--color-ink-muted)] min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label={menuOpen ? t('topbar.menu.close') : t('topbar.menu.open')}
          aria-expanded={menuOpen}
          aria-controls="portal-sidebar"
          className="portal-topbar-hamburger me-1 size-[30px] items-center justify-center rounded-[8px] border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] cursor-pointer transition-colors hover:text-[var(--color-ink)] hover:border-[var(--color-line-2)]"
        >
          <Menu className="size-3.5" aria-hidden />
        </button>
        <span className="portal-topbar-breadcrumb-org items-center min-w-0">
          <span className="truncate">{orgName.toUpperCase()}</span>
          <span className="ms-1.5 me-1 text-[var(--color-ink-faint)]" aria-hidden>
            /
          </span>
        </span>
        <b className="font-sans text-[13px] font-medium tracking-[-0.01em] text-[var(--color-ink)] truncate">
          {pageLabel}
        </b>
      </div>

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="portal-topbar-search hidden sm:inline-flex items-center gap-2 rounded-[8px] border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[12px] text-[var(--color-ink-muted)] cursor-text min-w-[220px] hover:border-[var(--color-line-2)]"
          aria-label={t('topbar.search.ariaLabel')}
        >
          <Search className="size-3.5" aria-hidden />
          <span>{t('topbar.search.placeholder')}</span>
          <kbd className="ms-auto rounded-[3px] border border-[var(--color-line-2)] bg-[var(--color-canvas)] px-1.5 py-px font-mono text-[10px] text-[var(--color-ink-muted)]">
            ⌘K
          </kbd>
        </button>

        <TopbarNotificationsBell />

        <button
          type="button"
          aria-label={t('topbar.help.ariaLabel')}
          title={t('topbar.help.ariaLabel')}
          className="inline-flex size-[30px] items-center justify-center rounded-[8px] border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink-muted)] cursor-pointer transition-colors hover:text-[var(--color-ink)] hover:border-[var(--color-line-2)]"
        >
          <HelpCircle className="size-3.5" aria-hidden />
        </button>

        <ProfileMenu />
      </div>
    </header>
  );
}

function deriveLabel(path: string): string {
  const last = path.split('/').filter(Boolean).pop() ?? '';
  if (!last) return '—';
  return last.charAt(0).toUpperCase() + last.slice(1);
}
